/**
 * Enhance Scope v2 (B-531) — sticky triage footer.
 *
 * Shows the live approved/rejected/open tally, a one-click "Approve all
 * Required (N)" for question-free required ADDs, and the batched Apply button
 * (disabled until at least one proposal is approved).
 */
import { Button } from '../ui/button';
import { IconCheck } from '../icons';

/** Props for {@link ApplyBar}. */
export interface ApplyBarProps {
  approved: number;
  rejected: number;
  open: number;
  /** Count of question-free required ADDs "Approve all Required" would check. */
  requiredCount: number;
  onApproveAllRequired: () => void;
  onApply: () => void;
  applying: boolean;
  /** True when ≥1 proposal is approved (Apply is enabled). */
  canApply: boolean;
}

/** Sticky bottom bar with the triage tally + Apply action. */
export function ApplyBar({
  approved,
  rejected,
  open,
  requiredCount,
  onApproveAllRequired,
  onApply,
  applying,
  canApply,
}: ApplyBarProps) {
  return (
    <div
      data-testid="enhance-apply-bar"
      className="sticky bottom-0 z-10 mt-3 flex flex-wrap items-center justify-between gap-3 rounded-[var(--r-lg)] border border-[var(--bd-subtle)] bg-[var(--bg-2)] px-4 py-[10px] shadow-[inset_0_1px_0_var(--hi-top),var(--sh-md)]"
    >
      <div className="text-[12.5px] text-[var(--tx-mid)]" data-testid="enhance-tally">
        <span className="font-[580] text-[var(--ok)]">{approved} approved</span>
        <span className="mx-1.5 text-[var(--tx-faint)]">·</span>
        <span className="text-[var(--tx-lo)]">{rejected} rejected</span>
        <span className="mx-1.5 text-[var(--tx-faint)]">·</span>
        <span className="text-[var(--tx-lo)]">{open} open</span>
      </div>
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          data-testid="enhance-approve-required"
          onClick={onApproveAllRequired}
          disabled={requiredCount === 0}
        >
          Approve all Required ({requiredCount})
        </Button>
        <Button
          size="sm"
          variant="primary"
          data-testid="enhance-apply"
          onClick={onApply}
          disabled={!canApply || applying}
        >
          <IconCheck />
          {applying ? 'Applying…' : 'Apply'}
        </Button>
      </div>
    </div>
  );
}

export default ApplyBar;
